// src/pages/Cart.js
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiTrash2, FiShoppingCart } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { toast } from "react-toastify";
// Prix degressif : MEME calcul que la page de paiement et que le serveur.
// Recalculer ici a la main ferait diverger les trois affichages.
import { cartItemUnitPrice, cartItemTotal, tierForQuantity } from "../utils/quantityTiers";

const getStableId = (item) => {
  if (item?.dealId && typeof item.dealId === "string") return item.dealId;
  if (item?.dealId && typeof item.dealId === "object") return item.dealId._id || item.dealId.id;
  return item?._id || item?.id || item?._localKey || null;
};

const Cart = () => {
  const { cartItems = [], updateQuantity, removeFromCart, clearCart, cartTotal = 0 } = useCart();
  const navigate = useNavigate();

  const items = Array.isArray(cartItems) ? cartItems : [];

  // ✅ PAS de Hook ici → pas d’erreur ESLint
  const totalItems = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <FiShoppingCart size={64} className="mx-auto text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Votre panier est vide</h2>
        <p className="text-gray-600 mb-8">
          Découvrez nos deals exceptionnels et profitez de réductions incroyables !
        </p>
        <Link
          to="/deals"
          className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700"
        >
          Explorer les deals
        </Link>
      </div>
    );
  }

  const subtotal =
    Number(cartTotal) ||
    items.reduce((s, it) => s + cartItemTotal(it), 0);

  // Economie totale rendue par les paliers, pour que le client voie tout de
  // suite ce que la quantite lui fait gagner.
  const savings = items.reduce(
    (s, it) =>
      s +
      Math.max(
        0,
        ((Number(it.basePrice ?? it.price) || 0) - cartItemUnitPrice(it)) *
          (Number(it.quantity) || 0)
      ),
    0
  );

  // Plus de ligne TVA ici. Elle ajoutait +20 % que la page de paiement
  // n'appliquait pas et que le serveur ne facturait pas : le panier annoncait
  // 456 MAD la ou la commande en debitait 361. Le total du panier doit etre
  // exactement celui du paiement.
  const total = subtotal;

  const handleDecrease = (item) => {
    const id = getStableId(item);
    if (!id) return;

    const qty = Number(item.quantity) || 1;
    const newQty = qty - 1;

    if (newQty <= 0) {
      if (window.confirm("Voulez-vous supprimer cet article du panier ?")) {
        removeFromCart(item);
        toast.success("Article retiré du panier");
      }
    } else {
      updateQuantity(item, newQty);
    }
  };

  const handleIncrease = (item) => {
    const id = getStableId(item);
    if (!id) return;

    const qty = Number(item.quantity) || 1;
    updateQuantity(item, qty + 1);
  };

  const handleRemove = (item) => {
    const id = getStableId(item);
    if (!id) return;
    removeFromCart(item);
    toast.success("Article retiré du panier");
  };

  const handleClear = () => {
    if (window.confirm("Vider le panier ? Cette action est irréversible.")) {
      clearCart();
      toast.success("Panier vidé");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">
          Mon Panier{" "}
          <span className="text-lg text-gray-500 ml-2">
            ({totalItems} article{totalItems > 1 ? "s" : ""})
          </span>
        </h1>

        <button
          onClick={handleClear}
          className="text-red-600 hover:text-red-700 font-semibold"
          aria-label="Vider le panier"
        >
          Vider le panier
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Items */}
        <div className="md:col-span-2 space-y-4">
          {items.map((item, idx) => {
            const id = item._localKey || getStableId(item) || `${idx}`;
            const qty = Math.max(1, Number(item.quantity) || 1);
            const basePrice = Number(item.basePrice ?? item.price) || 0;
            const price = cartItemUnitPrice(item);
            const tier = tierForQuantity(item.quantityTiers, qty);

            return (
              <div key={id} className="bg-white rounded-lg shadow p-4 flex">
                <img
                  src={item.image || "/placeholder.png"}
                  alt={item.title || "Deal image"}
                  className="w-24 h-24 object-cover rounded"
                />

                <div className="flex-1 ml-4">
                  <h3 className="font-semibold text-lg mb-2">{item.title || "Produit"}</h3>
                  {item.variantLabel && (
                    <p className="text-sm text-gray-500 mb-1">Option : {item.variantLabel}</p>
                  )}

                  <p className="text-primary-600 font-bold text-xl mb-2 flex items-center flex-wrap gap-2">
                    <span>{price.toFixed(2)} MAD</span>
                    {price < basePrice && (
                      <>
                        <span className="text-sm font-normal text-gray-400 line-through">
                          {basePrice.toFixed(2)} MAD
                        </span>
                        {tier && (
                          <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded">
                            -{tier.discountPercent}% des {tier.minQty}
                          </span>
                        )}
                      </>
                    )}
                  </p>

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDecrease(item)}
                        className="w-8 h-8 border rounded hover:bg-gray-100 flex items-center justify-center"
                        aria-label={`Diminuer la quantité de ${item.title || "cet article"}`}
                        title={qty <= 1 ? "Supprimer l'article" : "Diminuer"}
                      >
                        -
                      </button>

                      <span className="w-8 text-center font-semibold">{qty}</span>

                      <button
                        onClick={() => handleIncrease(item)}
                        className="w-8 h-8 border rounded hover:bg-gray-100 flex items-center justify-center"
                        aria-label={`Augmenter la quantité de ${item.title || "cet article"}`}
                        title="Augmenter"
                      >
                        +
                      </button>
                    </div>

                    <button
                      onClick={() => handleRemove(item)}
                      className="text-red-600 hover:text-red-700"
                      aria-label={`Retirer ${item.title || "cet article"} du panier`}
                      title="Supprimer"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <p className="font-bold text-xl">{cartItemTotal(item).toFixed(2)} MAD</p>
                  {price < basePrice && (
                    <p className="text-xs text-gray-400 line-through">
                      {(basePrice * qty).toFixed(2)} MAD
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Résumé */}
        <div>
          <div className="bg-white rounded-lg shadow p-6 sticky top-24">
            <h2 className="text-xl font-bold mb-2">Résumé</h2>
            <p className="text-sm text-gray-500 mb-4">
              {totalItems} article{totalItems > 1 ? "s" : ""} dans votre panier
            </p>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-gray-600">
                <span>Sous-total</span>
                <span>{subtotal.toFixed(2)} MAD</span>
              </div>

              {savings > 0 && (
                <div className="flex justify-between text-green-700 font-medium">
                  <span>Remise quantite</span>
                  <span>- {savings.toFixed(2)} MAD</span>
                </div>
              )}

              <div className="border-t pt-3 flex justify-between font-bold text-xl">
                <span>Total</span>
                <span className="text-primary-600">{total.toFixed(2)} MAD</span>
              </div>
            </div>

            <button
              onClick={() => navigate("/checkout")}
              className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 mb-3"
            >
              Passer la commande
            </button>

            <Link
              to="/deals"
              className="block text-center text-primary-600 hover:text-primary-700 font-semibold"
            >
              Continuer mes achats
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
